
export interface User {
  uid: string;
  displayName: string;
  communityName?: string; // Unique handle
  isPublicProfile?: boolean; // Controls global visibility
  email: string;
  photoURL: string;
  coverPhotoURL?: string;

  // Subscription Fields
  isSubscribed?: boolean;
  subscriptionRenewalDate?: string;
  stripeCustomerId?: string; // Link to Stripe
  stripeSubscriptionId?: string;
  communities?: string[]; // IDs of joined communities
  communitySettings?: { [communityId: string]: { shareLocation: boolean } }; // Per-community privacy
}

export interface CigarLog {
  id: string;
  brand: string;
  name: string;
  wrapper: string;
  originCountry?: string; // New field for cigar origin
  rating: number;
  notes: string;
  date: string;
  isPublic?: boolean; // Inherited from user profile at creation
  image?: string;
  price?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  userId?: string; // To link log to user in Firestore
  pairings?: DrinkPairing[]; // AI-generated pairing suggestions
  history?: string; // Brief history of the cigar/brand
  isLive?: boolean; // Whether this log is part of an active "Live Smoke" session
}

export interface DrinkPairing {
  type: 'whiskey' | 'rum' | 'coffee' | 'wine' | 'cognac';
  name: string;
  reason: string;
}

export interface TrendingCigar {
  name: string;
  brand: string;
  origin: string;
  strength: string;
  rating: number;
  description: string;
}

export interface PlaceResult {
  name: string;
  address: string;
  rating?: string;
  description?: string; // AI insight for this specific place
  uri?: string; // Google Maps URI
  geometry?: {
    location: {
      lat: number;
      lng: number;
    }
  };
}

export interface LoungeRating {
  id: string;
  placeName: string;
  rating: number;
  review: string;
  userId: string;
  timestamp: string;
}

// --- NEW FEATURES ---

export interface HumidorItem {
  id: string;
  brand: string;
  name: string;
  wrapper?: string;
  originCountry?: string;
  quantity: number;
  purchaseDate: string;
  storageLocation?: string; // e.g., "Desktop Humidor", "Tupperdor"
  notes?: string;
  image?: string;
  userId: string;
  // Aging Tracker fields
  agingStartDate?: string;
  targetAgingMonths?: number;
  agingNotes?: string;
  agingComplete?: boolean;
}

export interface WishlistItem {
  id: string;
  brand: string;
  name: string;
  addedDate: string;
  userId: string;
  notes?: string;
}

export interface Comment {
  id: string;
  userId: string;
  userName: string;
  text: string;
  timestamp: string;
}

export interface SocialLog extends CigarLog {
  likes: string[]; // Array of userIds who liked this
  comments: Comment[];
  userDisplayName?: string; // Denormalized for easy display
  userPhotoURL?: string;
}

export interface CheckIn {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL?: string;
  placeName: string;
  placeAddress: string;
  timestamp: string;
  cigarSmoked?: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  requirement: number;
  category: 'checkins' | 'diversity' | 'social';
}

export interface NextStickRecommendation {
  safePick: {
    brand: string;
    name: string;
    wrapper: string;
    origin: string;
    strength: string;
    reason: string;
    confidence: number;
  };
  adventurePick: {
    brand: string;
    name: string;
    wrapper: string;
    origin: string;
    strength: string;
    reason: string;
    adventureFactor: string;
  };
}

// --- COMMUNITIES ---

export interface Community {
  id: string;
  name: string;      // Unique, user-defined
  description: string;
  createdBy: string; // User UID
  admins: string[];  // UIDs of admins (creator is always admin)
  members: string[]; // UIDs of all members
  joinCode: string;  // Simple 6-char code for invites
  createdAt: string;
  memberCount: number; // Denormalized count
}

export interface CommunityMessage {
  id: string;
  communityId: string;
  senderId: string;
  senderName: string; // Denormalized for display
  senderPhotoURL?: string;
  text: string;
  timestamp: string;
  type: 'text' | 'system'; // 'system' for joined/left messages
}

export enum AppView {
  HOME = 'HOME',
  LOCATOR = 'LOCATOR',
  LOG_SMOKE = 'LOG_SMOKE',
  COMMUNITY = 'COMMUNITY',
  PROFILE = 'PROFILE',
  HUMIDOR = 'HUMIDOR',
  WISHLIST = 'WISHLIST',
  ANALYTICS = 'ANALYTICS',
  JOURNEY = 'JOURNEY',
  SOMMELIER = 'SOMMELIER',
  YOUR_NEXT_STICK = 'YOUR_NEXT_STICK',
  GALLERY = 'GALLERY',
  FLAVOR_PROFILE = 'FLAVOR_PROFILE',
  SETTINGS = 'SETTINGS',
  TERMS = 'TERMS',
}

export interface LiveSession {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL?: string;
  brand: string;
  name: string;
  startTime: string;
  currentNotes: string;
  activeMembers: string[]; // UIDs of people watching
}

export interface AgingInsight {
  peakAgingMonths: number;
  expectedFlavorProfile: string;
  currentMaturityScore: number; // 0-100
  recommendation: string;
}
